/**
 * ProcurementPostingBridge — Wave 1.
 *
 * Mounts INSIDE VendorPOProvider + WarehouseInventoryProvider + GL.
 * Sweeps receiving records and posts a GRNI accrual JE per receipt:
 *     DR 1200 WIP (job)  /  CR 2150 GRNI (vendor)
 *
 * Idempotency: keyed by receiving-record id. Re-running is a no-op
 * because GeneralLedgerContext.postJournal short-circuits on the
 * `idempotencyKey` derived from sourceRef.
 *
 * This bridge is intentionally separate from AutoPostingBridge so the
 * provider mount-order (GL → VendorPO → Warehouse) is respected.
 */
import { useEffect, useRef } from "react";
import { useVendorPO } from "@/data/VendorPOContext";
import { useWarehouseInventory } from "@/data/WarehouseInventoryContext";
import { useGeneralLedger } from "./GeneralLedgerContext";
import { ruleReceivedPO } from "./postingRules";

const key = (id: string) => `po_receipt::${id}`;

export function ProcurementPostingBridge() {
  const { purchaseOrders } = useVendorPO();
  const { receivingRecords } = useWarehouseInventory();
  const { journalEntries, postJournal } = useGeneralLedger();
  const postedRef = useRef<Set<string>>(new Set());

  // Keep posted-set in sync with the GL on every JE mutation.
  useEffect(() => {
    for (const je of journalEntries) {
      if (je.sourceRef?.kind === "po_receipt") postedRef.current.add(key(je.sourceRef.id));
    }
  }, [journalEntries]);

  useEffect(() => {
    for (const r of receivingRecords) {
      const k = key(r.id);
      if (postedRef.current.has(k)) continue;
      const po = purchaseOrders.find(p => p.poId === r.poId);
      if (!po) continue;
      const rule = ruleReceivedPO(po, r);
      if (!rule) continue;
      try {
        postJournal(rule);
        postedRef.current.add(k);
      } catch (e) {
        // eslint-disable-next-line no-console
        console.warn(`[ProcurementPostingBridge] receipt ${r.id}:`, e instanceof Error ? e.message : e);
      }
    }
  }, [receivingRecords, purchaseOrders, postJournal]);

  return null;
}
